import Link from "next/link";
import { ArrowRight, Check, CircleDot } from "lucide-react";
import type { AgendaItem, OperationsAgenda as Agenda } from "@/core/console/agenda";
import { cn } from "@/lib/utils";

// Phase 9.A — the calm home. Calm Operations Doctrine:
//  D5 one primary focus · D1 ≤5 on screen · D3 calm by default
//  D4 a digest, not a stream · D7 no false urgency (tone is steady)
export function OperationsAgenda({
  tenantId,
  agenda,
}: {
  tenantId: string;
  agenda: Agenda;
}) {
  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-10 px-6 py-12">
      {agenda.primaryFocus.kind === "all_clear" ? (
        <AllClear
          activeCount={agenda.primaryFocus.activeCount}
          handledCount={agenda.handledCount}
        />
      ) : (
        <PrimaryDecision
          tenantId={tenantId}
          item={agenda.primaryFocus.item}
        />
      )}

      {agenda.secondary.length > 0 && (
        <section className="flex flex-col gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">
            Also needs you
          </h2>
          <div className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {agenda.secondary.map((item) => (
              <SecondaryRow key={item.loadId} tenantId={tenantId} item={item} />
            ))}
          </div>
          {agenda.queuedCount > 0 && (
            <p className="text-sm text-muted-foreground">
              +{agenda.queuedCount} more in the queue — the agent is holding
              them until these clear.
            </p>
          )}
        </section>
      )}

      {agenda.primaryFocus.kind === "decision" && (
        <DigestLine
          watchingCount={agenda.watchingCount}
          handledCount={agenda.handledCount}
        />
      )}
    </div>
  );
}

function AllClear({
  activeCount,
  handledCount,
}: {
  activeCount: number;
  handledCount: number;
}) {
  return (
    <section className="flex flex-col items-center gap-4 py-16 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
        <Check size={26} />
      </div>
      <h1 className="text-2xl font-semibold tracking-tight">
        All loads on track
      </h1>
      <p className="max-w-md text-muted-foreground">
        {activeCount === 0
          ? "Nothing in motion right now. The agent will surface a load the moment it needs you."
          : `The agent is handling ${activeCount} active ${
              activeCount === 1 ? "load" : "loads"
            }. Nothing needs a decision from you.`}
      </p>
      {handledCount > 0 && (
        <p className="text-sm text-muted-foreground">
          Handled {handledCount} {handledCount === 1 ? "move" : "moves"} today.
        </p>
      )}
    </section>
  );
}

function PrimaryDecision({
  tenantId,
  item,
}: {
  tenantId: string;
  item: AgendaItem;
}) {
  const escalated = item.agentState === "escalated";
  return (
    <section className="flex flex-col gap-5 rounded-xl border border-border bg-card p-7 shadow-sm">
      <div
        className={cn(
          "text-xs font-semibold uppercase tracking-wide",
          escalated ? "text-red-600" : "text-amber-700",
        )}
      >
        {escalated ? "Escalated — needs you now" : "Decision needed"}
      </div>
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">
          {item.reference}
        </h1>
        <p className="text-muted-foreground">
          {item.route} · {item.customerName}
        </p>
      </div>
      {item.decisionLine && (
        <p className="rounded-lg bg-muted px-4 py-3 text-sm leading-relaxed">
          {item.decisionLine}
        </p>
      )}
      {item.why && (
        <p className="text-sm text-muted-foreground">Why: {item.why}</p>
      )}
      <div className="flex items-center gap-3 pt-1">
        <Link
          href={`/loads/${item.loadId}?tenantId=${tenantId}`}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:opacity-90"
        >
          Review &amp; decide
          <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
}

function SecondaryRow({
  tenantId,
  item,
}: {
  tenantId: string;
  item: AgendaItem;
}) {
  return (
    <Link
      href={`/loads/${item.loadId}?tenantId=${tenantId}`}
      className="flex items-center justify-between gap-4 px-4 py-3 transition hover:bg-muted/50"
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium">{item.reference}</span>
          <span
            className={cn(
              "text-xs",
              item.agentState === "escalated"
                ? "text-red-600"
                : "text-amber-700",
            )}
          >
            {item.label}
          </span>
        </div>
        <p className="truncate text-sm text-muted-foreground">
          {item.decisionLine ?? `${item.route} · ${item.customerName}`}
        </p>
      </div>
      <ArrowRight size={16} className="shrink-0 text-muted-foreground" />
    </Link>
  );
}

function DigestLine({
  watchingCount,
  handledCount,
}: {
  watchingCount: number;
  handledCount: number;
}) {
  if (watchingCount === 0 && handledCount === 0) return null;
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <CircleDot size={14} className="text-emerald-600" />
      {watchingCount > 0 && (
        <span>
          Agent is quietly watching {watchingCount}{" "}
          {watchingCount === 1 ? "other load" : "other loads"}
        </span>
      )}
      {watchingCount > 0 && handledCount > 0 && <span>·</span>}
      {handledCount > 0 && (
        <span>
          Handled {handledCount} {handledCount === 1 ? "move" : "moves"} today
        </span>
      )}
    </p>
  );
}
